import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Alert,
  Fade,
  useTheme,
  alpha,
  Snackbar,
  Paper,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tabs,
  Tab,
  Card,
  Button,
  CardContent,
  LinearProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  CalendarMonth as CalendarIcon,
  ViewDay as ViewDayIcon,
  ViewWeek as ViewWeekIcon,
  ViewModule as ViewModuleIcon,
  DateRange as DateRangeIcon,
  Lock as LockIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import CalendarView from './calendar-components/CalendarView';
import EventEditor from './calendar-components/EventEditor';

const SectionPaper = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(4),
  padding: theme.spacing(3),
  position: 'relative',
  borderRadius: 12,
  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  animation: 'slideInUp 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '@keyframes slideInUp': {
    '0%': {
      transform: 'translateY(100%)',
      opacity: 0,
    },
    '100%': {
      transform: 'translateY(0)',
      opacity: 1,
    }
  },
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.25)}`
  }
}));

const Calendar = () => {
  const theme = useTheme();
  const { companyId, eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { canAccessPage, canEditPage, canDeletePage } = usePermissions();
  const [events, setEvents] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventEditorOpen, setEventEditorOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [modalMode, setModalMode] = useState('create');
  const [timeFormat, setTimeFormat] = useState('12hour');
  
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentView, setCurrentView] = useState('month');
  
  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('error');

  // Helper function to show snackbar
  const showSnackbar = (message, severity = 'error') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Handle URL-based event editing and creation
  useEffect(() => {
    if (eventId && events.length > 0 && currentUser && companyId) {
      const eventToEdit = events.find(event => event.id === eventId);
      if (eventToEdit) {
        setSelectedEvent(eventToEdit);
        setModalMode('edit');
        setEventEditorOpen(true);
      } else {
        // Event not found, redirect to main calendar
        navigate(`/company/${companyId}/calendar`);
      }
    } else if (window.location.pathname.includes('/calendar/edit/new') && currentUser && companyId) {
      const defaultStartDate = location.state?.defaultStartDate || new Date();
      const prefilledData = location.state?.prefilledEventData;
      
      if (prefilledData) {
        // Use prefilled data from quick add "More Options"
        setSelectedEvent(prefilledData);
      } else {
        // Use default start date
        setSelectedEvent({ defaultStartDate });
      }
      setModalMode('create');
      setEventEditorOpen(true);
    } else if (!eventId && !window.location.pathname.includes('/calendar/edit/new')) {
      // No event ID and not creating new event, ensure editor is closed
      setEventEditorOpen(false);
      setSelectedEvent(null);
    }
  }, [eventId, events, currentUser, navigate, companyId, location.state]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('No authenticated user');

      // Get user profile
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();

      if (profileError || !profile) {
        throw new Error('User profile not found');
      }

      setCurrentUser({ ...profile, id: user.id });

      // Get company information
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .or(`created_by.eq.${user.id},allowed_users.cs.{${user.id}}`)
        .single();

      if (companyError || !companyData) {
        throw new Error('Company not found');
      }

      setCompany(companyData);

      // Fetch user preferences
      await fetchUserPreferences(user.id);

      // Fetch company users
      await fetchCompanyUsers(companyData);

      // Fetch events
      await fetchEvents(companyData.id);

    } catch (err) {
      console.error('Error in fetchInitialData:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async (companyId) => {
    try {
      // Get calendar events with attendees
      const { data: eventsData, error: eventsError } = await supabase
        .from('calendar_events')
        .select(`
          *,
          attendees:event_attendees(
            id,
            user_id,
            status,
            is_organizer,
            created_at
          )
        `)
        .eq('company_id', companyId)
        .order('start_date', { ascending: true });

      if (eventsError) {
        console.error('Error fetching events:', eventsError);
        throw eventsError;
      }

      // Get all unique attendee user IDs from all events
      const allAttendeeIds = [...new Set(
        (eventsData || [])
          .flatMap(event => event.attendees || [])
          .map(a => a.user_id)
      )];

      // Fetch user details for attendees if any exist
      let attendeeDetails = {};
      if (allAttendeeIds.length > 0) {
        try {
          const { data: usersData } = await supabase
            .from('profiles')
            .select('id, full_name, first_name, last_name')
            .in('id', allAttendeeIds);

          const { data: emailsData } = await supabase.rpc('get_user_emails_for_company', {
            p_company_id: companyId
          });

          allAttendeeIds.forEach(userId => {
            const userProfile = usersData?.find(u => u.id === userId);
            const userEmail = emailsData?.find(e => e.user_id === userId);
            attendeeDetails[userId] = {
              id: userId,
              full_name: userProfile?.full_name || userProfile?.first_name || userProfile?.last_name,
              email: userEmail?.email
            };
          });
        } catch (err) {
          console.warn('Error fetching attendee details:', err);
        }
      }

      // Add attendees to each event with user details
      const eventsWithAttendees = (eventsData || []).map(event => ({
        ...event,
        attendees: (event.attendees || []).map(attendee => ({
          ...attendee,
          user: attendeeDetails[attendee.user_id] || { id: attendee.user_id, email: 'Unknown' }
        }))
      }));

      setEvents(eventsWithAttendees);
    } catch (err) {
      console.error('fetchEvents error:', err);
      setError(err.message);
    }
  };

  const fetchCompanyUsers = async (companyData) => {
    try {
      // Get all user IDs from the company (creator + allowed users)
      const userIds = [...(companyData.allowed_users || []), companyData.created_by].filter(Boolean);
      
      if (userIds.length === 0) {
        setUsers([]);
        return;
      }

      // Get user profiles for all user IDs
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name, phone, role, created_at, updated_at')
        .in('id', userIds);

      if (profilesError) throw profilesError;

      // Get emails from auth.users using the security definer function
      const { data: emailsData, error: emailsError } = await supabase.rpc('get_user_emails_for_company', {
        p_company_id: companyData.id
      });

      if (emailsError) console.warn('Could not fetch emails:', emailsError);

      // Combine profile data with email data
      const enhancedUsers = (profilesData || []).map(profile => {
        const emailData = emailsData?.find(e => e.user_id === profile.id);
        
        return {
          ...profile,
          email: emailData?.email,
          last_sign_in_at: emailData?.last_sign_in_at
        };
      });

      setUsers(enhancedUsers);

    } catch (err) {
      console.error('Error fetching company users:', err);
    }
  };

  const fetchUserPreferences = async (userId) => {
    try {
      const { data: preferences, error: preferencesError } = await supabase
        .from('user_preferences')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (preferencesError && preferencesError.code !== 'PGRST116') {
        throw preferencesError;
      }

      if (preferences) {
        setTimeFormat(preferences.time_format);
      } else {
        // Create default preferences if none exist
        await createUserPreferences(userId);
      }
    } catch (err) {
      console.error('Error fetching user preferences:', err);
    }
  };

  const createUserPreferences = async (userId) => {
    try {
      const { error } = await supabase
        .from('user_preferences')
        .upsert([{
          user_id: userId,
          time_format: '12hour'
        }], {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setTimeFormat('12hour');
    } catch (err) {
      console.error('Error creating user preferences:', err);
    }
  };

  const saveTimeFormat = async (newTimeFormat) => {
    try {
      if (!currentUser) return;
      
      const { error } = await supabase
        .from('user_preferences')
        .upsert([{
          user_id: currentUser.id,
          time_format: newTimeFormat
        }], {
          onConflict: 'user_id'
        });

      if (error) throw error;
      setTimeFormat(newTimeFormat);
    } catch (err) {
      console.error('Error saving time format:', err);
      showSnackbar('Failed to save time format preference');
    }
  };

  const handleCreateEvent = (eventData = null) => {
    if (eventData) {
      // Quick event creation from calendar
      handleSaveEvent(eventData);
    } else if (companyId) {
      // Navigate to new event URL
      navigate(`/company/${companyId}/calendar/edit/new`);
    }
  };

  const handleEditEvent = (event) => {
    if (companyId) {
      navigate(`/company/${companyId}/calendar/edit/${event.id}`);
    }
  };

  const handleSaveEvent = async (eventData) => {
    try {
      // Robust validation for UUIDs
      const validateUUID = (uuid, fieldName) => {
        if (!uuid || uuid === 'undefined' || typeof uuid !== 'string' || uuid.length < 30) {
          throw new Error(`Invalid ${fieldName}: ${uuid}`);
        }
        return uuid;
      };
      
      // Validate required data with proper error messages
      const validCompanyId = validateUUID(company?.id, 'company ID');
      const validUserId = validateUUID(currentUser?.id, 'user ID');
      
      // Determine if this is create or update based on whether event has an ID
      const isCreateMode = !eventData.id;
      if (isCreateMode) {
        // Exclude attendees from event data since they're handled in separate table
        const { attendees, ...eventDataWithoutAttendees } = eventData;
        
        // Clean the event data to remove any undefined values
        const cleanedEventData = Object.fromEntries(
          Object.entries(eventDataWithoutAttendees).filter(([key, value]) => 
            value !== undefined && value !== "undefined" && value !== null && value !== ""
          )
        );
        
        const insertData = {
          ...cleanedEventData,
          company_id: validCompanyId,
          created_by: validUserId
        };
                
        // Final UUID validation check on all fields
        Object.entries(insertData).forEach(([key, value]) => {
          if (value === 'undefined' || (typeof value === 'string' && value.includes('undefined'))) {
            console.error(`FOUND UNDEFINED IN FIELD: ${key} =`, value);
            throw new Error(`Invalid UUID in field ${key}: ${value}`);
          }
        });
        
        const { data: newEvent, error } = await supabase
          .from('calendar_events')
          .insert([insertData])
          .select()
          .single();
        
        if (error) {
          console.error('Database insert error:', error);
          throw error;
        }

        // Handle attendees
        if (eventData.attendees && eventData.attendees.length > 0) {
          // Filter out attendees with undefined user_id
          const validAttendees = eventData.attendees.filter(attendee => {
            if (!attendee || !attendee.user_id || typeof attendee.user_id !== 'string' || 
                attendee.user_id === 'undefined' || attendee.user_id.length < 30) {
              console.warn('Filtering out invalid attendee:', attendee);
              return false;
            }
            return true;
          });
             
          if (validAttendees.length > 0) {
            const attendeeData = validAttendees.map(attendee => {
              // Validate each attendee user_id
              const validAttendeeUserId = validateUUID(attendee.user_id, `attendee user_id`);
              
              return {
                event_id: newEvent.id,
                user_id: validAttendeeUserId,
                company_id: validCompanyId,
                invited_by: validUserId,
                status: attendee.user_id === validUserId ? 'accepted' : 'pending',
                is_organizer: attendee.user_id === validUserId
              };
            });
            
            const { error: attendeeError } = await supabase
              .from('event_attendees')
              .insert(attendeeData);

            if (attendeeError) {
              console.error('Attendee insert error:', attendeeError);
              throw attendeeError;
            }
          }
        }

        // Handle recurring events - generate them manually
        if (eventData.is_recurring && eventData.recurrence_end_date) {
          const startDate = new Date(eventData.start_date);
          const endDate = new Date(eventData.end_date);
          const duration = endDate.getTime() - startDate.getTime();
          const recurEndDate = new Date(eventData.recurrence_end_date);
          
          let currentStart = new Date(startDate);
          const recurringEvents = [];
          let count = 0;
          const maxEvents = 365; // Increase limit for yearly events
          
          // Advance to first recurrence (skip the original event which was already inserted)
          switch (eventData.recurrence_pattern) {
            case 'daily':
              currentStart.setDate(currentStart.getDate() + 1);
              break;
            case 'weekly':
              currentStart.setDate(currentStart.getDate() + 7);
              break;
            case 'monthly':
              currentStart.setMonth(currentStart.getMonth() + 1);
              break;
            case 'yearly':
              currentStart.setFullYear(currentStart.getFullYear() + 1);
              break;
          }
          
          // Generate recurring events
          while (currentStart <= recurEndDate && count < maxEvents) {
            const currentEnd = new Date(currentStart.getTime() + duration);
            
            recurringEvents.push({
              company_id: validCompanyId,
              title: eventData.title,
              description: eventData.description || '',
              start_date: currentStart.toISOString(),
              end_date: currentEnd.toISOString(),
              all_day: eventData.all_day,
              location: eventData.location,
              videocall_url: eventData.videocall_url,
              meeting_password: eventData.meeting_password,
              created_by: validUserId,
              privacy: eventData.privacy,
              status: eventData.status,
              is_recurring: false,
              parent_event_id: newEvent.id
            });
            
            // Calculate next occurrence
            switch (eventData.recurrence_pattern) {
              case 'daily':
                currentStart.setDate(currentStart.getDate() + 1);
                break;
              case 'weekly':
                currentStart.setDate(currentStart.getDate() + 7);
                break;
              case 'monthly':
                currentStart.setMonth(currentStart.getMonth() + 1);
                break;
              case 'yearly':
                currentStart.setFullYear(currentStart.getFullYear() + 1);
                break;
            }
            count++;
          }
          
          if (recurringEvents.length > 0) {
            const { error: recurringError } = await supabase
              .from('calendar_events')
              .insert(recurringEvents);
            
            if (recurringError) {
              console.error('Error creating recurring events:', recurringError);
              throw new Error(`Failed to create recurring events: ${recurringError.message}`);
            }
            
            console.log(`Successfully created ${recurringEvents.length} recurring events`);
          }
        }
      } else {
        // Update mode - only validate event ID for existing events
        if (!eventData.id || eventData.id === 'undefined' || typeof eventData.id !== 'string' || eventData.id.length < 30) {
          throw new Error('Invalid event ID for update mode: ' + eventData.id);
        }
        const validEventId = eventData.id;
        
        // Exclude attendees from event data since they're handled in separate table
        const { attendees, ...eventDataWithoutAttendees } = eventData;
        
        const { error } = await supabase
          .from('calendar_events')
          .update(eventDataWithoutAttendees)
          .eq('id', validEventId);
        
        if (error) throw error;

        // Handle attendees update
        if (eventData.attendees) {
          // Delete existing attendees and re-add them
          await supabase
            .from('event_attendees')
            .delete()
            .eq('event_id', validEventId);

          if (eventData.attendees.length > 0) {
            // Filter out attendees with undefined user_id
            const validAttendees = eventData.attendees.filter(attendee => {
              if (!attendee || !attendee.user_id || typeof attendee.user_id !== 'string' || 
                  attendee.user_id === 'undefined' || attendee.user_id.length < 30) {
                console.warn('Filtering out invalid attendee:', attendee);
                return false;
              }
              return true;
            });
            
            if (validAttendees.length > 0) {
              const attendeeData = validAttendees.map(attendee => {
                const validAttendeeUserId = validateUUID(attendee.user_id, `attendee user_id`);
                
                return {
                  event_id: validEventId,
                  user_id: validAttendeeUserId,
                  company_id: validCompanyId,
                  invited_by: validUserId,
                  status: attendee.status || 'pending',
                  is_organizer: attendee.is_organizer || false
                };
              });
              
              const { error: attendeeError } = await supabase
                .from('event_attendees')
                .insert(attendeeData);

              if (attendeeError) throw attendeeError;
            }
          }
        }
      }

      // Refresh events
      await fetchEvents(validCompanyId);
      setEventEditorOpen(false);
      showSnackbar('Event saved successfully!', 'success');
      // Navigate back to calendar
      navigate(`/company/${companyId}/calendar`);
    } catch (err) {
      console.error('=== ERROR SAVING EVENT ===');
      console.error('Error saving event:', err);
      console.error('Error message:', err.message);
      console.error('Company state:', company);
      console.error('CurrentUser state:', currentUser);
      console.error('CompanyId param:', companyId);
      showSnackbar(`Failed to save event: ${err.message}`);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    try {
      const { error } = await supabase
        .from('calendar_events')
        .delete()
        .eq('id', eventId);
      
      if (error) throw error;
      
      await fetchEvents(company.id);
      setEventEditorOpen(false);
      showSnackbar('Event deleted successfully', 'success');
      // Navigate back to calendar
      navigate(`/company/${companyId}/calendar`);
    } catch (err) {
      console.error('Error deleting event:', err);
      showSnackbar(`Failed to delete event: ${err.message}`);
    }
  };

  const handleDateClick = (dateOrEventData) => {
    if (typeof dateOrEventData === 'object' && dateOrEventData.title) {
      // Quick event creation
      handleCreateEvent(dateOrEventData);
    } else {
      // Navigate to new event URL with selected date
      navigate(`/company/${companyId}/calendar/edit/new`, { 
        state: { defaultStartDate: dateOrEventData } 
      });
    }
  };

  // Filter events based on search and filters
  const filteredEvents = events.filter(event => {
    // Search filter
    if (searchQuery && !event.title.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !event.description?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }

    // Status filter
    if (statusFilter !== 'all' && event.status !== statusFilter) {
      return false;
    }

    return true;
  });

  if (loading) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.2)}`
          }
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{ 
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha(theme.palette.primary.main, 0.1),
                mb: 2
              }}>
                <CalendarIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{ 
              fontWeight: 500,
              color: 'primary.main',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Calendar
            </Typography>
            <Typography variant="body2" sx={{ 
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing calendar events and scheduling data...
            </Typography>
            <Box sx={{ 
              width: '60%', 
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'primary.main',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl">
        <Alert severity="error" sx={{ mt: 2, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`, borderRadius: 3 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  // Check if user can access calendar
  if (!canAccessPage('calendar')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <CalendarIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Calendar Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Calendar. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }

  if (eventEditorOpen) {
    return (
      <EventEditor
        event={selectedEvent}
        onSave={handleSaveEvent}
        onDelete={handleDeleteEvent}
        onCancel={() => companyId && navigate(`/company/${companyId}/calendar`)}
        users={users}
        currentUser={currentUser}
        company={company}
        mode={modalMode}
        timeFormat={timeFormat}
        canEdit={canEditPage('calendar')}
        canDelete={canDeletePage('calendar')}
      />
    );
  }

  return (
    <Fade in timeout={800}>
      <Container 
        maxWidth="xl" 
        sx={{
          py: 3,
          pb: 8,
          px: { xs: 2, sm: 3 },
          width: '100%',
          maxWidth: '100vw !important'
        }}
      >
        {/* Filters and Controls */}
        <SectionPaper sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
              {/* Search */}
              <TextField
                size="small"
                placeholder="Search events..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ 
                  width: 220,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 3,
                    backgroundColor: 'white',
                    height: 40,
                    fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[300], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />

              {/* Status Filter */}
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel sx={{ fontSize: '0.9rem' }}>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        minWidth: 140,
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: 'white',
                    height: 40,
                    fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[300], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="all">All Status</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="tentative">Tentative</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>

              {/* Time Format */}
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.9rem' }}>Time</InputLabel>
                <Select
                  value={timeFormat}
                  label="Time"
                  onChange={(e) => saveTimeFormat(e.target.value)}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        minWidth: 150,
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: 'white',
                    height: 40,
                    fontSize: '0.9rem',
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: alpha(theme.palette.grey[300], 0.8)
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderWidth: '1px',
                      borderColor: theme.palette.primary.main
                    }
                  }}
                >
                  <MenuItem value="12hour">12 Hour</MenuItem>
                  <MenuItem value="24hour">24 Hour</MenuItem>
                </Select>
              </FormControl>
            </Box>

            {/* View Toggle */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', fontSize: '0.9rem' }}>
                View:
              </Typography>
              <Tabs
                value={currentView}
                onChange={(e, newValue) => setCurrentView(newValue)}
                sx={{ 
                  minHeight: 'auto',
                  '& .MuiTabs-root': {
                    minHeight: 40
                  },
                  '& .MuiTab-root': {
                    borderRadius: 3,
                    minHeight: 40,
                    minWidth: 'auto',
                    px: 2,
                    py: 1,
                    fontWeight: 500,
                    fontSize: '0.85rem',
                    textTransform: 'none',
                    border: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                    backgroundColor: 'white',
                    color: 'text.secondary',
                    mr: 1,
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      borderColor: theme.palette.primary.main
                    },
                  },
                  '& .MuiTabs-indicator': {
                    display: 'none'
                  }
                }}
              >
                <Tab
                  icon={<ViewDayIcon sx={{ fontSize: 18 }} />}
                  value="day"
                  label="Day"
                  iconPosition="start"
                />
                <Tab
                  icon={<ViewWeekIcon sx={{ fontSize: 18 }} />}
                  value="week"
                  label="Week"
                  iconPosition="start"
                />
                <Tab
                  icon={<ViewModuleIcon sx={{ fontSize: 18 }} />}
                  value="month"
                  label="Month"
                  iconPosition="start"
                />
                <Tab
                  icon={<DateRangeIcon sx={{ fontSize: 18 }} />}
                  value="year"
                  label="Year"
                  iconPosition="start"
                />
              </Tabs>
            </Box>
          </Box>
        </SectionPaper>

        {/* Main Content */}
        <CalendarView
          events={filteredEvents}
          onEventClick={handleEditEvent}
          onDateClick={handleDateClick}
          onCreateEvent={handleCreateEvent}
          onDeleteEvent={handleDeleteEvent}
          timeFormat={timeFormat}
          currentView={currentView}
          users={users}
          currentUser={currentUser}
          canEdit={canEditPage('calendar')}
          canDelete={canDeletePage('calendar')}
        />

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={4000}
          onClose={() => setSnackbarOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbarOpen(false)}
            severity={snackbarSeverity}
            sx={{
              width: '100%',
              borderRadius: 2,
              fontWeight: 500
            }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Fade>
  );
};

export default Calendar;
